<template>
    <Head title="Pending Checklist Tasks" />

    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <!-- Header -->
        <div class="flex justify-between items-center mb-6">
            <div>
                <h1 class="text-2xl font-semibold text-gray-900 dark:text-white">Pending Checklist Tasks</h1>
                <p class="mt-1 text-sm text-gray-600 dark:text-gray-400">Checklists assigned to you or pending your approval</p>
            </div>
        </div>

        <!-- Tabs -->
        <div class="border-b border-gray-200 dark:border-gray-700 mb-6">
            <nav class="-mb-px flex space-x-8">
                <Link
                    :href="route('checklists.my-tasks') + '?tab=all'"
                    :class="[
                        currentTab === 'all'
                            ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                            : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300',
                        'whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm cursor-pointer'
                    ]"
                >
                    All
                    <span v-if="counts.all" class="ml-2 py-0.5 px-2 rounded-full text-xs bg-blue-100 dark:bg-blue-900 text-blue-600 dark:text-blue-300">
                        {{ counts.all }}
                    </span>
                </Link>
                <Link
                    :href="route('checklists.my-tasks') + '?tab=to_conduct'"
                    :class="[
                        currentTab === 'to_conduct'
                            ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                            : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300',
                        'whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm cursor-pointer'
                    ]"
                >
                    To Conduct
                    <span v-if="counts.to_conduct" class="ml-2 py-0.5 px-2 rounded-full text-xs bg-blue-100 dark:bg-blue-900 text-blue-600 dark:text-blue-300">
                        {{ counts.to_conduct }}
                    </span>
                </Link>
                <Link
                    :href="route('checklists.my-tasks') + '?tab=in_progress'"
                    :class="[
                        currentTab === 'in_progress'
                            ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                            : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300',
                        'whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm cursor-pointer'
                    ]"
                >
                    In Progress
                    <span v-if="counts.in_progress" class="ml-2 py-0.5 px-2 rounded-full text-xs bg-blue-100 dark:bg-blue-900 text-blue-600 dark:text-blue-300">
                        {{ counts.in_progress }}
                    </span>
                </Link>
                <Link
                    :href="route('checklists.my-tasks') + '?tab=to_review'"
                    :class="[
                        currentTab === 'to_review'
                            ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                            : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300',
                        'whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm cursor-pointer'
                    ]"
                >
                    To Review
                    <span v-if="counts.to_review" class="ml-2 py-0.5 px-2 rounded-full text-xs bg-blue-100 dark:bg-blue-900 text-blue-600 dark:text-blue-300">
                        {{ counts.to_review }}
                    </span>
                </Link>
            </nav>
        </div>

        <!-- Checklist List -->
        <div class="bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700">
            <!-- Empty State -->
            <div v-if="!checklists.data || checklists.data.length === 0" class="text-center py-12">
                <svg class="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                <h3 class="mt-2 text-sm font-medium text-gray-900 dark:text-white">No tasks</h3>
                <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">
                    {{ getEmptyMessage() }}
                </p>
            </div>

            <!-- Checklist Table -->
            <div v-else class="overflow-x-auto">
                <table class="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                    <thead class="bg-gray-50 dark:bg-gray-900">
                        <tr>
                            <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                Checklist
                            </th>
                            <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                Template
                            </th>
                            <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                Status
                            </th>
                            <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                Due Date
                            </th>
                            <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                Action
                            </th>
                        </tr>
                    </thead>
                    <tbody class="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                        <tr v-for="checklist in checklists.data" :key="checklist.id" class="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                            <td class="px-6 py-4 whitespace-nowrap">
                                <div class="flex items-center">
                                    <div>
                                        <div class="text-sm font-medium text-gray-900 dark:text-white">
                                            {{ checklist.checklist_number }}
                                        </div>
                                        <div v-if="checklist.location" class="text-sm text-gray-500 dark:text-gray-400">
                                            {{ checklist.location.name }}
                                        </div>
                                    </div>
                                </div>
                            </td>
                            <td class="px-6 py-4">
                                <div class="text-sm text-gray-900 dark:text-white">
                                    {{ checklist.template?.title || 'N/A' }}
                                </div>
                            </td>
                            <td class="px-6 py-4 whitespace-nowrap">
                                <span :class="getStatusClass(checklist.status)">
                                    {{ getStatusLabel(checklist.status) }}
                                </span>
                            </td>
                            <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                                <div v-if="checklist.due_date">
                                    {{ formatDate(checklist.due_date) }}
                                    <span v-if="isDueSoon(checklist.due_date)" class="ml-2 text-xs text-orange-600 dark:text-orange-400">
                                        (Due soon)
                                    </span>
                                    <span v-else-if="isOverdue(checklist.due_date)" class="ml-2 text-xs text-red-600 dark:text-red-400">
                                        (Overdue)
                                    </span>
                                </div>
                                <span v-else class="text-gray-400">No due date</span>
                            </td>
                            <td class="px-6 py-4 whitespace-nowrap text-sm">
                                <div v-if="checklist.status === 'assigned' || checklist.status === 'in_progress'" class="flex items-center gap-2">
                                    <Link
                                        :href="route('checklists.conduct', checklist.id)"
                                        class="inline-flex items-center px-3 py-1 bg-green-600 text-white text-xs font-medium rounded hover:bg-green-700"
                                    >
                                        {{ checklist.status === 'assigned' ? 'Start Now' : 'Continue' }}
                                    </Link>
                                    <Link
                                        :href="route('checklists.show', checklist.id)"
                                        class="inline-flex items-center px-3 py-1 bg-gray-600 text-white text-xs font-medium rounded hover:bg-gray-700"
                                    >
                                        View
                                    </Link>
                                </div>
                                <Link
                                    v-else-if="checklist.status === 'pending_approval' || checklist.status === 'pending_review'"
                                    :href="route('checklists.show', checklist.id)"
                                    class="inline-flex items-center px-3 py-1 bg-blue-600 text-white text-xs font-medium rounded hover:bg-blue-700"
                                >
                                    Review & Approve
                                </Link>
                                <Link
                                    v-else
                                    :href="route('checklists.show', checklist.id)"
                                    class="text-blue-600 dark:text-blue-400 hover:text-blue-900 dark:hover:text-blue-300"
                                >
                                    View
                                </Link>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <!-- Pagination -->
            <div v-if="checklists.data && checklists.data.length > 0" class="px-6 py-4 border-t border-gray-200 dark:border-gray-700">
                <Pagination :links="checklists.links" />
            </div>
        </div>
    </div>
</template>

<script setup>
import { Head, Link } from '@inertiajs/vue3';
import Pagination from '@/Components/Pagination.vue';
import { useTimezone } from '@/Composables/useTimezone';

const props = defineProps({
    checklists: Object,
    currentTab: {
        type: String,
        default: 'all'
    },
    counts: {
        type: Object,
        default: () => ({
            all: 0,
            to_conduct: 0,
            in_progress: 0,
            to_review: 0
        })
    }
});

const { formatDateTime: formatDateTimeInTZ } = useTimezone();

const getEmptyMessage = () => {
    if (props.currentTab === 'to_conduct') {
        return 'You have no checklists to conduct at the moment.';
    } else if (props.currentTab === 'in_progress') {
        return 'You have no checklists in progress.';
    } else if (props.currentTab === 'to_review') {
        return 'You have no checklists pending your review.';
    }
    return 'You have no current tasks.';
};

const getStatusClass = (status) => {
    const classes = {
        assigned: 'px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
        in_progress: 'px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
        pending_approval: 'px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200',
        completed: 'px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
        rejected: 'px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
        cancelled: 'px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200',
    };
    return classes[status] || classes.assigned;
};

const getStatusLabel = (status) => {
    const labels = {
        assigned: 'Assigned',
        in_progress: 'In Progress',
        pending_approval: 'Pending Approval',
        completed: 'Completed',
        rejected: 'Rejected',
        cancelled: 'Cancelled',
    };
    return labels[status] || status;
};

const formatDate = (date) => {
    if (!date) return 'N/A';
    return formatDateTimeInTZ(date);
};

const isDueSoon = (dueDate) => {
    if (!dueDate) return false;
    const due = new Date(dueDate);
    const now = new Date();
    const diffHours = (due - now) / (1000 * 60 * 60);
    return diffHours > 0 && diffHours <= 24; // Due within 24 hours
};

const isOverdue = (dueDate) => {
    if (!dueDate) return false;
    const due = new Date(dueDate);
    const now = new Date();
    return due < now;
};
</script>
